// usePrivacyOptout — the CCPA/CPRA "Do Not Sell or Share" opt-out (B1/B2).
// Reads/sets the user's opt-out (record-backed, IP-stamped) and auto-honors the
// Global Privacy Control (GPC) browser signal. Opted-out users are dropped from
// the sellable sentiment aggregate server-side (rpc_sentiment_aggregate).
import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useUserProfile } from "@/hooks/useUserProfile";

export interface PrivacyPrefs {
  do_not_sell_share: boolean;
  opt_out_source: "manual" | "gpc" | null;
  opted_out_at: string | null;
}

const PRIVACY_PREFS_KEY = ["privacy-prefs"] as const;

/** True if the browser is sending a Global Privacy Control opt-out signal. */
export function browserHasGpc(): boolean {
  try {
    // navigator.globalPrivacyControl is the standardized property; some browsers
    // also expose it on window.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nav = navigator as any;
    return nav?.globalPrivacyControl === true;
  } catch {
    return false;
  }
}

export function usePrivacyPrefs(enabled = true) {
  return useQuery({
    queryKey: PRIVACY_PREFS_KEY,
    enabled,
    queryFn: async (): Promise<PrivacyPrefs> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("get_my_privacy_prefs");
      if (error) throw error;
      const row = (data ?? [])[0];
      return {
        do_not_sell_share: !!row?.do_not_sell_share,
        opt_out_source: row?.opt_out_source ?? null,
        opted_out_at: row?.opted_out_at ?? null,
      };
    },
    staleTime: 5 * 60 * 1000,
  });
}

export function useSetPrivacyOptout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { optOut: boolean; source?: "manual" | "gpc" }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase.rpc as any)("set_privacy_optout", {
        p_opt_out: vars.optOut,
        p_source: vars.source ?? "manual",
        p_user_agent: typeof navigator !== "undefined" ? navigator.userAgent : null,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: PRIVACY_PREFS_KEY }),
  });
}

/** Mounted once app-wide. If the browser sends GPC and the signed-in user isn't
 *  already opted out, record an opt-out (source='gpc') automatically — the law
 *  requires honoring GPC as a valid opt-out. Never re-opts-in. */
export function useGpcAutoOptOut() {
  const { session } = useUserProfile();
  const gpc = browserHasGpc();
  const enabled = !!session && gpc;
  const { data: prefs } = usePrivacyPrefs(enabled);
  const setOptout = useSetPrivacyOptout();
  const ranRef = useRef(false);

  useEffect(() => {
    if (!enabled || !prefs || ranRef.current) return;
    if (prefs.do_not_sell_share) return; // already opted out — nothing to do
    ranRef.current = true;
    setOptout.mutate({ optOut: true, source: "gpc" });
  }, [enabled, prefs, setOptout]);
}
